import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { MessageSquare, Trash2, CornerDownLeft, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Internal Comments Thread — chat-style history shared across the whole
 * application. Every comment shows who wrote it and when.
 *
 * Enter submits the comment; Shift+Enter inserts a newline.
 * Comments render in chronological order (oldest first, newest at the bottom).
 * Authors and admins can delete their own comments.
 */
export default function InternalCommentsThread({ pack, onPackChange, currentUserId, currentUserName, variant = 'card' }) {
  const { token, user } = useAuth();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const listEndRef = useRef(null);
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const uid = currentUserId || user?.id;
  const uname = currentUserName || user?.name || user?.email;
  const isAdmin = user?.role === 'admin' || user?.role === 'director';

  const comments = useMemo(() => {
    // Sort chronologically ascending (oldest first) for a natural thread flow
    return [...(pack.internal_comments || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [pack.internal_comments]);

  // Auto-scroll to the newest comment WITHIN the message list only — never
  // scroll the outer page. Skip the very first render so opening the pack
  // doesn't hijack the broker's viewport and jump them down to the thread.
  const didMountRef = useRef(false);
  const prevCountRef = useRef(comments.length);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      prevCountRef.current = comments.length;
      return;
    }
    // Only auto-scroll when a new comment was actually added (not on removals)
    if (comments.length > prevCountRef.current && listEndRef.current) {
      const container = listEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
    prevCountRef.current = comments.length;
  }, [comments.length]);

  const submit = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/internal-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      setDraft('');
    } catch (e) {
      toast.error(`Failed to post comment — ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (commentId) => {
    if (!confirm('Delete this comment?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/internal-comments/${commentId}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      toast.success('Comment removed');
    } catch (e) {
      toast.error(`Delete failed — ${e.message}`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={variant === 'drawer' ? 'h-full flex flex-col' : 'rounded-lg overflow-hidden'}
      data-testid="internal-comments-thread"
      style={variant === 'drawer' ? { background: '#ffffff' } : { background: '#ffffff', border: '1px solid #e2e8f0' }}
    >
      {/* Header strip — hidden inside the drawer (drawer has its own header) */}
      {variant !== 'drawer' && (
        <div className="px-4 py-2 flex items-center justify-between border-b" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: '#dc2626' }} />
            <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: '#7f1d1d' }}>Internal comment thread</span>
          </div>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: '#991b1b' }}>{comments.length} comment{comments.length === 1 ? '' : 's'}</span>
        </div>
      )}

      {/* Comment list */}
      <div
        className={variant === 'drawer' ? 'flex-1 overflow-y-auto p-3 space-y-2' : 'max-h-[420px] overflow-y-auto p-3 space-y-2'}
        style={{ background: '#fefdfd' }}
      >
        {comments.length === 0 ? (
          <div className="text-center py-6 text-[12px] italic" style={{ color: '#94a3b8' }} data-testid="no-comments">
            No comments yet. Start the internal thread below.
          </div>
        ) : (
          comments.map((c) => (
            <CommentBubble key={c.id} comment={c} isMine={c.user_id === uid} canDelete={c.user_id === uid || isAdmin} onDelete={() => remove(c.id)} />
          ))
        )}
        <div ref={listEndRef} />
      </div>

      {/* Composer */}
      <div className="p-3 border-t shrink-0" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: '#dc2626' }} title={uname}>
            <span className="text-[10px] font-black" style={{ color: '#ffffff' }}>{(uname || '?').charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Write a comment… press Enter to send, Shift+Enter for a new line."
              rows={2}
              className="text-sm resize-none"
              style={{ color: '#0f172a' }}
              data-testid="internal-comments-composer"
              disabled={busy}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px]" style={{ color: '#94a3b8' }}>
                <CornerDownLeft className="w-3 h-3 inline" /> Enter to send
              </span>
              <Button size="sm" onClick={submit} disabled={busy || !draft.trim()}
                className="bg-rose-600 hover:bg-rose-700 text-white h-7 text-[11px] px-3" data-testid="internal-comments-send">
                {busy ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentBubble({ comment, isMine, canDelete, onDelete }) {
  const [hover, setHover] = useState(false);
  const initial = (comment.user_name || '?').charAt(0).toUpperCase();
  const ts = comment.created_at ? new Date(comment.created_at) : null;
  const dateLabel = ts ? ts.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div
      className="flex items-start gap-2"
      style={{ flexDirection: isMine ? 'row-reverse' : 'row' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`comment-${comment.id}`}
    >
      {/* Avatar */}
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ background: isMine ? '#2563eb' : '#64748b' }}
        title={comment.user_name}
      >
        <span className="text-[10px] font-black" style={{ color: '#ffffff' }}>{initial}</span>
      </div>
      {/* Bubble */}
      <div className="max-w-[85%]" style={{ textAlign: isMine ? 'right' : 'left' }}>
        <div className="text-[10.5px] font-semibold mb-0.5" style={{ color: '#64748b' }}>
          <UserIcon className="w-2.5 h-2.5 inline mr-1" />
          <b style={{ color: '#334155' }}>{comment.user_name || 'Unknown'}</b>
          <span className="mx-1">·</span>
          <span className="tabular-nums" title={dateLabel}>{dateLabel}</span>
        </div>
        <div
          className="rounded-2xl px-3.5 py-2 whitespace-pre-wrap break-words inline-block text-left"
          style={{
            background: isMine ? '#dbeafe' : '#f1f5f9',
            border: `1px solid ${isMine ? '#93c5fd' : '#e2e8f0'}`,
            color: '#0f172a',
          }}
        >
          <div className="text-[13px] leading-snug">{comment.text}</div>
        </div>
        {canDelete && hover && (
          <button
            onClick={onDelete}
            className="text-[10px] font-bold ml-2 mt-0.5 inline-flex items-center gap-0.5 hover:underline"
            style={{ color: '#e11d48' }}
            data-testid={`delete-comment-${comment.id}`}
          >
            <Trash2 className="w-2.5 h-2.5" /> Delete
          </button>
        )}
      </div>
    </div>
  );
}
